/**
 * Analyse d'une image de caméra : est-elle obstruée ?
 *
 * Le calcul et les seuils viennent de la détection d'obstruction de la caméra
 * tuteur (P-1260), éprouvée en séance. Ils sont extraits ici pour que le test
 * matériel d'entrée au live s'appuie sur exactement la même mesure, plutôt que
 * d'inventer une seconde heuristique qui dériverait de la première.
 *
 * Fichier pur : aucun import, aucune dépendance React ou LiveKit. C'est ce qui
 * permet de l'utiliser aussi bien depuis le hook in-séance que depuis l'écran
 * d'entrée, et de le tester sans monter de composant.
 */

/** Une image de 32×24 suffit : on cherche une tendance, pas du détail. */
export const CANVAS_WIDTH = 32
export const CANVAS_HEIGHT = 24

/**
 * Trois relevés concordants avant de conclure. La cadence, elle, appartient à
 * l'appelant : la surveillance en séance peut prendre son temps, le test
 * d'entrée doit trancher pendant que l'élève regarde.
 */
export const CONSECUTIVE_SAMPLES_TO_CONFIRM = 3

/**
 * Luminance moyenne (0-255) en dessous de laquelle l'image paraît noire.
 * Un visage filmé en intérieur normal se situe entre 80 et 150 ; un objectif
 * couvert descend sous 25.
 */
const LUMINANCE_MEAN_THRESHOLD = 25

/**
 * Variance : un objectif couvert donne une image uniforme (variance proche de
 * zéro), alors qu'une pièce même mal éclairée garde de la texture (> 200).
 * C'est ce second critère qui évite d'accuser une chambre sombre.
 */
const LUMINANCE_VARIANCE_THRESHOLD = 50

/**
 * Moyenne et variance de luminance d'une image RGBA.
 *
 * @param {Uint8ClampedArray} pixels données RGBA telles que renvoyées par getImageData
 * @param {Float32Array|number[]} buffer tampon réutilisé entre les relevés
 * @returns {{ mean: number, variance: number }}
 */
export function luminanceStats(pixels, buffer) {
    const n = buffer.length
    let sum = 0

    for (let i = 0; i < n; i += 1) {
        const r = pixels[i * 4]
        const g = pixels[i * 4 + 1]
        const b = pixels[i * 4 + 2]
        // Pondération perceptuelle : l'œil est bien plus sensible au vert.
        const y = 0.299 * r + 0.587 * g + 0.114 * b
        buffer[i] = y
        sum += y
    }

    const mean = sum / n

    let varSum = 0
    for (let i = 0; i < n; i += 1) {
        const d = buffer[i] - mean
        varSum += d * d
    }

    return { mean, variance: varSum / n }
}

/** Une image est jugée obstruée si elle est à la fois sombre ET uniforme. */
export function isFrameObstructed({ mean, variance }) {
    return mean < LUMINANCE_MEAN_THRESHOLD && variance < LUMINANCE_VARIANCE_THRESHOLD
}
